
import { motion } from "framer-motion";
import {
  Scissors,
  ArrowRight,
  Play,
  Heart,
  ChevronDown,
} from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import Blob from "@/components/ui/Blob";
import StitchDots from "@/components/ui/StitchDots";
import ThreadDecoration from "@/components/ui/ThreadDecoration";

export default function ChelseaIntroSection() {
  const theme = usePalette();
  return (
    <section
      className="relative min-h-dvh flex items-center overflow-hidden pt-20"
      style={{ backgroundColor: colors.cream }}
    >
      <Blob className="w-96 h-96 top-10 -left-20" color={theme.blob1} />
      <Blob className="w-80 h-80 bottom-10 right-0" color={theme.blob2} />
      <Blob className="w-64 h-64 top-1/2 left-1/3" color={theme.blob3} />
      <StitchDots />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Text side */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center lg:text-left"
          >
            <motion.div
              variants={fadeUp}
              custom={0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs tracking-widest uppercase mb-6"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                backgroundColor: `${theme.accentSoft}40`,
                color: theme.accent,
              }}
            >
              <Scissors size={14} />
              Sewist &middot; Creator &middot; Community Builder
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-5xl sm:text-6xl lg:text-7xl leading-[1.08] mb-6"
              style={{
                fontFamily: fonts.logo,
                fontWeight: 400,
                color: colors.navy,
              }}
            >
              Hey, I&apos;m{" "}
              <span
                className="relative inline-block"
                style={{ color: theme.accent }}
              >
                Chelsea
                <motion.svg
                  className="absolute -bottom-1 left-0 w-full"
                  viewBox="0 0 200 12"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.8 }}
                >
                  <motion.path
                    d="M2 8 Q50 2, 100 7 T198 5"
                    stroke={theme.warm}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="6 4"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 0.8 }}
                  />
                </motion.svg>
              </span>
              <span style={{ color: theme.warm }}>!</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-lg sm:text-xl leading-relaxed mb-3 max-w-lg mx-auto lg:mx-0"
              style={{
                fontFamily: fonts.heading,
                fontWeight: 400,
                color: theme.accent,
                fontSize: "1.6rem",
              }}
            >
              Loose Threads, Big Ideas
            </motion.p>

            <motion.p
              variants={fadeUp}
              custom={3}
              className="text-base leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0"
              style={{
                fontFamily: fonts.body,
                fontWeight: 500,
                color: colors.navy,
                opacity: 0.7,
                letterSpacing: "0.03em",
              }}
            >
              Tying the threads of creativity together. Welcome to my little
              corner of the internet where sewing meets community and good vibes
              flow freely.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={4}
              className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start"
            >
              <a
                href="#content"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white text-sm tracking-wide cursor-pointer transition-all duration-250 hover:shadow-xl hover:scale-105 active:scale-[0.98]"
                style={{
                  fontFamily: fonts.button,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase" as const,
                  fontSize: "0.75rem",
                  backgroundColor: theme.accent,
                }}
              >
                See What I&apos;m About
                <ArrowRight size={16} />
              </a>
              <a
                href="#connect"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-wide border-2 cursor-pointer transition-all duration-200 hover:bg-[#606b4c]/5"
                style={{
                  fontFamily: fonts.button,
                  fontWeight: 700,
                  borderColor: `${colors.navy}20`,
                  color: colors.navy,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase" as const,
                  fontSize: "0.75rem",
                }}
              >
                <Play size={14} />
                Watch My Latest
              </a>
            </motion.div>

            {/* Social proof mini */}
            <motion.div
              variants={fadeUp}
              custom={5}
              className="flex items-center gap-6 mt-10 justify-center lg:justify-start"
            >
              {[
                { num: "200K+", label: "Community" },
                { num: "500+", label: "Videos" },
                { num: "50+", label: "Collabs" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p
                    className="text-xl"
                    style={{
                      fontFamily: fonts.logo,
                      fontWeight: 700,
                      color: theme.warm,
                    }}
                  >
                    {stat.num}
                  </p>
                  <p
                    className="text-xs tracking-wide uppercase"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 600,
                      color: `${colors.navy}80`,
                      letterSpacing: "0.08em",
                    }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{
              duration: 0.7,
              delay: 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="relative flex justify-center lg:justify-end"
          >
            {/* Background shape */}
            <div
              className="absolute -inset-4 rounded-[2.5rem] -rotate-3"
              style={{
                background: `linear-gradient(135deg, ${theme.blob1}40, ${theme.blob2}30, ${theme.blob3}40)`,
              }}
            />
            <div className="relative">
              <img
                src="/chelsea.JPG"
                alt="Chelsea — sewist, creator, and community builder"
                width={448}
                height={597}
                className="relative z-10 w-full max-w-md rounded-[2rem] shadow-2xl object-cover aspect-[3/4]"
                style={{
                  boxShadow: `0 25px 50px -12px ${theme.blob1}60`,
                }}
              />
              {/* Floating badges */}
              <motion.div
                className="absolute -top-4 -right-4 z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2"
                style={{ backgroundColor: colors.cream }}
                animate={{ y: [0, -6, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3,
                  ease: "easeInOut",
                }}
              >
                <Heart
                  size={18}
                  style={{ color: colors.berryPink }}
                  fill={colors.berryPink}
                />
                <span
                  className="text-sm"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: colors.navy,
                  }}
                >
                  200K Fam
                </span>
              </motion.div>
              <motion.div
                className="absolute -bottom-3 -left-4 z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2"
                style={{ backgroundColor: colors.cream }}
                animate={{ y: [0, 6, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3.5,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
              >
                <Scissors size={18} style={{ color: theme.accent }} />
                <span
                  className="text-sm"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: colors.navy,
                  }}
                >
                  Creating Daily
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Thread decoration */}
      <div className="absolute bottom-16 left-0 right-0 z-10 px-12">
        <ThreadDecoration className="w-full opacity-40" />
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
      >
        <ChevronDown size={24} style={{ color: `${colors.navy}40` }} />
      </motion.div>
    </section>
  );
}
